import { useNavigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useDepartment } from '@magimp/shared-hooks';
import { useUsersList } from '@magimp/shared-hooks';
import {
  Button,
  ErrorState,
  LoadingState,
  EmptyState,
  StatusBadge,
  RowCard,
  useNarrow,
} from '../../../ui/index.js';
import { errorMessage } from '../../../api/errors.js';

export function DepartmentDetailPage() {
  const { t } = useTranslation(['departments', 'common', 'users']);
  const narrow = useNarrow();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const deptQ = useDepartment(id ?? '');
  const usersQ = useUsersList(id ? { departmentId: id } : {});

  if (deptQ.isPending) return <LoadingState />;
  if (deptQ.error)
    return (
      <ErrorState
        title={t('common:states.loadError')}
        text={errorMessage(deptQ.error)}
        onRetry={() => void deptQ.refetch()}
      />
    );
  if (!deptQ.isSuccess) return null;
  const d = deptQ.data;
  const totalCount = usersQ.data?.length ?? 0;
  const activeCount = (usersQ.data ?? []).filter((u) => u.active).length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minHeight: 0, flex: 1 }}>
      <header style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <Button kind="ghost" onClick={() => navigate('/departments')}>
          {t('departments:detail.back')}
        </Button>
        <h1 style={{ fontSize: 20, fontWeight: 600, margin: 0, flex: 1 }}>{d.name}</h1>
        {d.archivedAt ? <StatusBadge status="archive" /> : <StatusBadge status="active" />}
      </header>

      <div style={{ fontSize: 12, color: 'var(--fg-2)' }}>
        {t('departments:detail.employees', { total: totalCount, active: activeCount })}
      </div>

      {usersQ.error && (
        <ErrorState
          title={t('departments:detail.loadEmployeesError')}
          text={errorMessage(usersQ.error)}
          onRetry={() => void usersQ.refetch()}
        />
      )}
      {usersQ.isPending && <LoadingState />}
      {usersQ.isSuccess && usersQ.data.length === 0 && (
        <EmptyState
          title={t('common:states.emptyTitle')}
          text={t('departments:detail.emptyEmployees')}
        />
      )}
      {usersQ.isSuccess &&
        usersQ.data.length > 0 &&
        (narrow ? (
          <ul
            style={{
              listStyle: 'none',
              margin: 0,
              padding: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}
          >
            {usersQ.data.map((u) => (
              <li key={u.id} style={{ listStyle: 'none' }}>
                <RowCard
                  onSelect={() => navigate(`/users/${u.id}`)}
                  ariaLabel={u.name}
                  header={
                    <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--fg-1)' }}>
                      {u.name}
                    </div>
                  }
                  actions={<StatusBadge status={u.active ? 'active' : 'inactive'} />}
                  fields={[{ label: 'Email', value: u.email }]}
                />
              </li>
            ))}
          </ul>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ textAlign: 'left', color: 'var(--fg-2)', fontSize: 12 }}>
                <th style={{ padding: '8px 6px' }}>{t('users:fields.name')}</th>
                <th style={{ padding: '8px 6px' }}>Email</th>
                <th style={{ padding: '8px 6px' }}>{t('users:fields.status')}</th>
              </tr>
            </thead>
            <tbody>
              {usersQ.data.map((u) => (
                <tr
                  key={u.id}
                  style={{ borderTop: '1px solid var(--border-2)', cursor: 'pointer' }}
                  onClick={() => navigate(`/users/${u.id}`)}
                >
                  <td style={{ padding: '10px 6px' }}>{u.name}</td>
                  <td style={{ padding: '10px 6px' }}>{u.email}</td>
                  <td style={{ padding: '10px 6px' }}>
                    {u.active ? <StatusBadge status="active" /> : <StatusBadge status="inactive" />}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ))}
    </div>
  );
}
